import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, Minus, Plus, RefreshCw } from "lucide-react";

import Axios from "../../../../../api/axios/Axios";
import { generateUniqueId } from "../../../../../utils/helpers/generator/generateUniqueId";
import { s3Uploader } from "../../../../../utils/helpers/s3Uploader/s3Uploader";

import { FormLayout } from "../../../../../components/layout/forms/FormLayout";
import FormHeader from "../../../../../components/layout/forms/header/FormHeader";
import Heading from "../../../../../components/typography/Heading";
import Text from "../../../../../components/typography/Text";
import Button from "../../../../../components/buttons/Button";

import { updateTalentPhoto } from "../../services/userTalentService";

import "./TalentProfileUploadForm.css";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const OUTPUT_SIZE = 400;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const uploadToS3 = (s3Path, file, filename) =>
  new Promise((resolve, reject) => {
    let settled = false;

    const complete = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    try {
      Promise.resolve(
        s3Uploader(
          s3Path,
          {
            file,
            filename,
            contentType: file.type,
          },
          0,
          () => {},
          complete,
        ),
      ).catch(fail);
    } catch (error) {
      fail(error);
    }
  });

export default function TalentProfileUploadForm({
  talentId,
  onClose,
  onRefresh,
}) {
  const [image, setImage] = useState("");
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const inputRef = useRef(null);
  const imageRef = useRef(null);
  const viewportRef = useRef(null);
  const maskRef = useRef(null);
  const dragRef = useRef({ x: 0, y: 0 });
  const uploadingRef = useRef(false);

  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setDragging(false);

    return () => {
      if (image) URL.revokeObjectURL(image);
    };
  }, [image]);

  const selectFile = (file) => {
    if (!file || uploadingRef.current) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      setError("Select a JPG, PNG or WebP image.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("Image size must be 20 MB or less.");
      return;
    }

    setError("");
    setImage(URL.createObjectURL(file));
  };

  const handleFileChange = (event) => {
    selectFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    selectFile(event.dataTransfer.files?.[0]);
  };

  const startDrag = (event) => {
    if (!image || uploading || event.button !== 0) return;

    event.preventDefault();

    dragRef.current = {
      x: event.clientX - position.x,
      y: event.clientY - position.y,
    };

    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!dragging || uploading) return;

    const img = imageRef.current;
    const viewport = viewportRef.current;
    const mask = maskRef.current;

    if (!img?.naturalWidth || !viewport || !mask) return;

    const viewportRect = viewport.getBoundingClientRect();
    const maskRect = mask.getBoundingClientRect();

    const baseScale = Math.max(
      viewportRect.width / img.naturalWidth,
      viewportRect.height / img.naturalHeight,
    );

    const displayedWidth = img.naturalWidth * baseScale * zoom;
    const displayedHeight = img.naturalHeight * baseScale * zoom;

    const maxX = Math.max(0, (displayedWidth - maskRect.width) / 2);

    const maxY = Math.max(0, (displayedHeight - maskRect.height) / 2);

    setPosition({
      x: clamp(event.clientX - dragRef.current.x, -maxX, maxX),
      y: clamp(event.clientY - dragRef.current.y, -maxY, maxY),
    });
  };

  const stopDrag = () => {
    setDragging(false);
  };

  const handleZoom = (value) => {
    setZoom(Number(clamp(value, 1, 4).toFixed(2)));
  };

  const createCroppedPhoto = async () => {
    const img = imageRef.current;
    const viewport = viewportRef.current;
    const mask = maskRef.current;

    if (!img?.naturalWidth || !viewport || !mask) {
      throw new Error("Please select a valid image.");
    }

    const viewportRect = viewport.getBoundingClientRect();
    const maskRect = mask.getBoundingClientRect();

    const imageWidth = img.naturalWidth;
    const imageHeight = img.naturalHeight;

    const viewportWidth = viewportRect.width;
    const viewportHeight = viewportRect.height;

    const baseScale = Math.max(
      viewportWidth / imageWidth,
      viewportHeight / imageHeight,
    );

    const totalScale = baseScale * zoom;

    const displayedWidth = imageWidth * totalScale;
    const displayedHeight = imageHeight * totalScale;

    const imageLeft = (viewportWidth - displayedWidth) / 2 + position.x;

    const imageTop = (viewportHeight - displayedHeight) / 2 + position.y;

    const cropLeft = maskRect.left - viewportRect.left;
    const cropTop = maskRect.top - viewportRect.top;

    const sourceX = (cropLeft - imageLeft) / totalScale;
    const sourceY = (cropTop - imageTop) / totalScale;
    const sourceWidth = maskRect.width / totalScale;
    const sourceHeight = maskRect.height / totalScale;

    const canvas = document.createElement("canvas");

    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;

    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Unable to process the image.");
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    context.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error("Unable to crop the image.")),
        "image/jpeg",
        0.6,
      );
    });
  };

  const handleSave = async () => {
    if (!image || !talentId || uploadingRef.current) return;

    uploadingRef.current = true;
    setUploading(true);
    setError("");

    try {
      const blob = await createCroppedPhoto();
      const filename = `${generateUniqueId()}.jpg`;

      const file = new File([blob], filename, {
        type: "image/jpeg",
      });

      const { data: s3Path } = await Axios.get("/api/talent/avatar/s3-url");

      await uploadToS3(s3Path, file, filename);
      await updateTalentPhoto(talentId, filename);

      await onRefresh?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to upload profile photo.",
      );
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  return (
    <FormLayout>
      <FormHeader
        title="Update Profile Photo"
        description="Upload, crop and position your Talent ID photo."
      />

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        disabled={uploading}
        hidden
      />

      {error && (
        <div className="talent-profile-upload__error" role="alert">
          <Text as="p" size="sm">
            {error}
          </Text>
        </div>
      )}

      {image && (
        <Heading as="h3" size="sm">
          Adjust Photo
        </Heading>
      )}

      <div
        ref={viewportRef}
        className={[
          "talent-profile-upload__viewport",
          dragging && "is-dragging",
          dragOver && "is-drag-over",
          uploading && "is-uploading",
        ]
          .filter(Boolean)
          .join(" ")}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onLostPointerCapture={stopDrag}
        onDragOver={(event) => {
          event.preventDefault();
          if (!uploading) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {!image ? (
          <button
            type="button"
            className="talent-profile-upload__placeholder"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <span className="talent-profile-upload__placeholder-icon">
              <ImagePlus size={28} />
            </span>

            <span>Choose or drop an image</span>

            <small>JPG, PNG or WebP · Max 20 MB</small>
          </button>
        ) : (
          <div className="talent-profile-upload__preview">
            <img
              ref={imageRef}
              src={image}
              alt="Profile photo preview"
              className="talent-profile-upload__image"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              }}
              draggable={false}
            />

            <div ref={maskRef} className="talent-profile-upload__crop-mask" />
          </div>
        )}
      </div>

      {image && (
        <>
          <div className="talent-profile-upload__controls">
            <div className="talent-profile-upload__zoom">
              <button
                type="button"
                className="talent-profile-upload__icon-button"
                onClick={() => handleZoom(zoom - 0.1)}
                disabled={uploading || zoom <= 1}
                aria-label="Zoom out"
              >
                <Minus size={16} />
              </button>

              <input
                type="range"
                min="1"
                max="4"
                step="0.01"
                value={zoom}
                onChange={(event) => handleZoom(Number(event.target.value))}
                disabled={uploading}
                aria-label="Image zoom"
              />

              <button
                type="button"
                className="talent-profile-upload__icon-button"
                onClick={() => handleZoom(zoom + 0.1)}
                disabled={uploading || zoom >= 4}
                aria-label="Zoom in"
              >
                <Plus size={16} />
              </button>
            </div>

            <button
              type="button"
              className="talent-profile-upload__icon-button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              aria-label="Change image"
              title="Change image"
            >
              <RefreshCw size={17} />
            </button>
          </div>

          <Text
            as="p"
            tone="secondary"
            size="sm"
            className="talent-profile-upload__hint"
          >
            Drag to reposition and use the slider to zoom.
          </Text>
        </>
      )}

      <div className="talent-profile-upload__footer">
        <Button
          value="Cancel"
          variant="primary-outlined"
          onClick={onClose}
          disabled={uploading}
        />

        <Button
          value={uploading ? "Uploading..." : "Save Photo"}
          variant="primary"
          onClick={handleSave}
          disabled={!image || uploading}
        />
      </div>
    </FormLayout>
  );
}
